import React, { useState, useEffect } from 'react';
import { Box, Slider, TextField, Typography } from '@mui/material';

interface ThresholdSliderProps {
    low: number;
    high: number;
    onChange: (low: number, high: number) => void;
}

export const ThresholdSlider: React.FC<ThresholdSliderProps> = ({ low, high, onChange }) => {
    const [localValue, setLocalValue] = useState<number[]>([low, high]);

    useEffect(() => {
        setLocalValue([low, high]);
    }, [low, high]);

    const handleSliderChange = (_: Event, newValue: number | number[]) => {
        setLocalValue(newValue as number[]);
    };

    const handleSliderCommit = () => {
        onChange(localValue[0], localValue[1]);
    };

    const handleLowInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = Number(e.target.value);
        const clampedToZero = Math.max(0, raw);
        const value = Math.min(clampedToZero, localValue[1] - 1);
        setLocalValue([value, localValue[1]]);
    };

    const handleHighInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = Number(e.target.value);
        const clampedToZero = Math.max(0, raw);
        const value = Math.max(clampedToZero, localValue[0] + 1);
        setLocalValue([localValue[0], value]);
    };

    const handleInputBlur = () => {
        onChange(localValue[0], localValue[1]);
    };

    return (
        <Box data-testid="threshold-slider" sx={{ px: 1 }}>
            <Box sx={{ position: 'relative', pt: 5, pb: 1 }}>
                <Slider
                    data-testid="threshold-slider-input"
                    value={localValue}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleSliderCommit}
                    min={0}
                    max={100}
                    valueLabelDisplay="on"
                    sx={{
                        color: 'warning.dark',
                        '& .MuiSlider-valueLabel': {
                            top: 52,
                            bottom: 'unset',
                            '&::before': {
                                top: -8,
                                bottom: 'unset',
                            },
                        },
                        '& .MuiSlider-thumb:nth-of-type(1)': { color: 'success.dark' },
                        '& .MuiSlider-thumb:nth-of-type(3)': { color: 'error.dark' },
                        '& .MuiSlider-track': { color: 'warning.dark' },
                        '& .MuiSlider-rail': (theme) => ({
                            background: `linear-gradient(to right, ${theme.palette.success.dark}, ${theme.palette.warning.dark}, ${theme.palette.error.dark})`,
                            opacity: 1,
                        }),
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 4, mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'success.dark', flexShrink: 0 }} />
                    <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
                        Low Score Threshold:
                    </Typography>
                    <TextField
                        data-testid="threshold-slider-low-input"
                        size="small"
                        type="number"
                        value={localValue[0]}
                        onChange={handleLowInput}
                        onBlur={handleInputBlur}
                        sx={{ width: 90, flexShrink: 0 }}
                        slotProps={{
                            htmlInput: { min: 0, style: { fontSize: '0.9rem' } },
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'error.dark', flexShrink: 0 }} />
                    <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
                        High Score Threshold:
                    </Typography>
                    <TextField
                        data-testid="threshold-slider-high-input"
                        size="small"
                        type="number"
                        value={localValue[1]}
                        onChange={handleHighInput}
                        onBlur={handleInputBlur}
                        sx={{ width: 90, flexShrink: 0 }}
                        slotProps={{
                            htmlInput: { min: 0, style: { fontSize: '0.9rem' } },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};